// ── Shortcut Registry ────────────────────────────────────────────────────────
// Pure functions, no React dependencies.

// ── Platform Detection ──────────────────────────────────────────────────────

export type Platform = 'mac' | 'windows' | 'linux'

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'linux'
  const ua = navigator.userAgent
  if (/Mac|iPhone|iPad|iPod/.test(ua)) return 'mac'
  if (/Win/.test(ua)) return 'windows'
  return 'linux'
}

export const platform: Platform = detectPlatform()

/** Returns the platform-appropriate modifier label: "Cmd" on Mac, "Ctrl" on others */
export function modLabel(): string {
  return platform === 'mac' ? 'Cmd' : 'Ctrl'
}

export type ShortcutCategory = 'General' | 'Layout' | 'Editor'

export interface ShortcutDef {
  id: string
  label: string
  category: ShortcutCategory
  defaultKeys: string
  /** Platform-specific overrides keyed by platform */
  platformKeys?: Partial<Record<Platform, string>>
  description?: string
}

export const DEFAULT_SHORTCUTS: ShortcutDef[] = [
  // General
  { id: 'app.commandPalette', label: 'Command Palette', category: 'General', defaultKeys: 'mod+k' },
  { id: 'app.newNote', label: 'New Note', category: 'General', defaultKeys: 'mod+shift+k' },
  { id: 'app.settings', label: 'Settings', category: 'General', defaultKeys: 'mod+,' },

  // Layout
  { id: 'layout.toggleSidebar', label: 'Toggle Sidebar', category: 'Layout', defaultKeys: 'mod+\\' },
  { id: 'layout.toggleInspector', label: 'Toggle Inspector', category: 'Layout', defaultKeys: 'mod+shift+\\' },

  // Editor
  { id: 'editor.find', label: 'Find in Note', category: 'Editor', defaultKeys: 'mod+f' },
  { id: 'editor.fullscreen', label: 'Toggle Fullscreen', category: 'Editor', defaultKeys: 'mod+shift+f' },
  { id: 'editor.bold', label: 'Bold', category: 'Editor', defaultKeys: 'mod+b' },
  { id: 'editor.italic', label: 'Italic', category: 'Editor', defaultKeys: 'mod+i' },
  { id: 'editor.strikethrough', label: 'Strikethrough', category: 'Editor', defaultKeys: 'mod+shift+x' },
  { id: 'editor.toggleTodo', label: 'Toggle Task Done', category: 'Editor', defaultKeys: 'mod+enter' },
  { id: 'editor.code', label: 'Inline Code', category: 'Editor', defaultKeys: 'mod+e' },
  { id: 'editor.link', label: 'Insert Link', category: 'Editor', defaultKeys: 'mod+shift+l' },
]

const shortcutMap = new Map(DEFAULT_SHORTCUTS.map((s) => [s.id, s]))

export function getShortcutDef(id: string): ShortcutDef | undefined {
  return shortcutMap.get(id)
}

/** Returns the effective default keys for a shortcut on the current platform */
export function getDefaultKeys(def: ShortcutDef): string {
  return def.platformKeys?.[platform] ?? def.defaultKeys
}

// ── Parsing ──────────────────────────────────────────────────────────────────

export interface ParsedKeyCombo {
  mod: boolean   // Cmd on Mac, Ctrl on others
  ctrl: boolean  // explicit Ctrl (rare)
  alt: boolean
  shift: boolean
  key: string    // lowercase key name
}

export function parseKeyCombo(combo: string): ParsedKeyCombo {
  const parts = combo.toLowerCase().split('+')
  const key = parts[parts.length - 1]
  return {
    mod: parts.includes('mod'),
    ctrl: parts.includes('ctrl'),
    alt: parts.includes('alt'),
    shift: parts.includes('shift'),
    key,
  }
}

// ── Matching ─────────────────────────────────────────────────────────────────

const isMac = platform === 'mac'

export function matchesKeyEvent(combo: string, event: KeyboardEvent): boolean {
  const parsed = parseKeyCombo(combo)

  // "mod" means metaKey on Mac, ctrlKey on others
  const modPressed = isMac ? event.metaKey : event.ctrlKey
  if (parsed.mod && !modPressed) return false
  if (!parsed.mod && modPressed) return false

  // explicit ctrl (separate from mod)
  if (parsed.ctrl && !event.ctrlKey) return false

  if (parsed.shift !== event.shiftKey) return false
  if (parsed.alt !== event.altKey) return false

  // Normalize the event key
  const eventKey = event.key.toLowerCase()

  // Handle backslash — event.key is "\" but we store as "\\"
  if (parsed.key === '\\') return eventKey === '\\'

  return eventKey === parsed.key
}

// ── Formatting ───────────────────────────────────────────────────────────────

const MAC_SYMBOLS: Record<string, string> = {
  mod: '⌘',
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
}

const PC_SYMBOLS: Record<string, string> = {
  mod: 'Ctrl',
  ctrl: 'Ctrl',
  alt: 'Alt',
  shift: 'Shift',
}

const KEY_DISPLAY: Record<string, string> = {
  '\\': '\\',
  enter: '↵',
  escape: 'Esc',
  backspace: '⌫',
  delete: 'Del',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
}

export function formatKeyCombo(combo: string, mac?: boolean): string {
  const useMac = mac ?? isMac
  const symbols = useMac ? MAC_SYMBOLS : PC_SYMBOLS
  const parts = combo.toLowerCase().split('+')
  const key = parts[parts.length - 1]
  const modifiers = parts.slice(0, -1)

  const formattedMods = modifiers.map((m) => symbols[m] ?? m)
  const formattedKey = KEY_DISPLAY[key] ?? key.toUpperCase()

  if (useMac) {
    return formattedMods.join('') + formattedKey
  }
  return [...formattedMods, formattedKey].join('+')
}

// ── Conflict Detection ───────────────────────────────────────────────────────

export function detectConflict(
  id: string,
  newCombo: string,
  allBindings: Record<string, string>,
): string | null {
  const normalizedNew = newCombo.toLowerCase()
  for (const [actionId, combo] of Object.entries(allBindings)) {
    if (actionId === id) continue
    if (combo.toLowerCase() === normalizedNew) return actionId
  }
  return null
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Build a resolved bindings map from defaults + overrides */
export function resolveBindings(overrides: Record<string, string>): Record<string, string> {
  const resolved: Record<string, string> = {}
  for (const def of DEFAULT_SHORTCUTS) {
    resolved[def.id] = overrides[def.id] ?? getDefaultKeys(def)
  }
  return resolved
}
